import { useState, useRef } from 'react';

export default function ContactFormSection() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);
    const honeypotVal = (formData.get('phone_alt') as string || '').trim();
    if (honeypotVal) {
      setStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    setStatus('loading');
    setErrorMsg('');
    const source = typeof window !== 'undefined' ? window.location.pathname : '';
    formData.append('source', source);
    formData.append('_subject', `Nouveau contact DFPRO Hérault — ${source}`);
    try {
      const response = await fetch('https://formspree.io/f/xwvgaedz', { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      if (response.ok) { form.reset(); window.location.href = '/merci/'; }
      else {
        let msg = "Une erreur est survenue lors de l'envoi. Réessayez ou appelez le 09 80 80 22 42.";
        try { const data = await response.json(); if (data?.errors?.length) msg = data.errors.map((x: { message: string }) => x.message).join(' '); } catch { /* ignore */ }
        setErrorMsg(msg); setStatus('error');
      }
    } catch { setErrorMsg('Erreur de connexion. Réessayez ou appelez le 09 80 80 22 42.'); setStatus('error'); }
  };

  return (
    <section className="relative bg-background-50 py-14 md:py-20 overflow-hidden">
      {/* Subtle decorative background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-500/[0.03] rounded-full translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent-500/[0.04] rounded-full -translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 md:px-6">
        {/* Section header */}
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-flex items-center gap-2 text-accent-500 font-label font-semibold text-xs uppercase tracking-widest mb-3">
            <span className="w-5 h-px bg-accent-500" />
            Devis gratuit
            <span className="w-5 h-px bg-accent-500" />
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-primary-500 mb-3">
            Un problème de menuiserie ?
          </h2>
          <p className="text-secondary-500 font-body text-sm md:text-base max-w-xl mx-auto">
            Décrivez-nous votre panne, on s'occupe de tout. Intervention sous 24 à 48h dans tout l'Hérault.
          </p>
        </div>

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-0 lg:gap-0 bg-white rounded-xl overflow-hidden border border-background-200/70">
          {/* Left column - Image + info */}
          <div className="lg:w-5/12 relative overflow-hidden">
            <img
              src="/images/contact-form-hero-01.jpg"
              alt="Technicien DFPRO en intervention de réparation de baie vitrée"
              className="w-full h-64 lg:h-full object-cover object-top"
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-primary-500/90 via-primary-500/40 to-primary-500/10 flex flex-col justify-end p-6 md:p-8">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500">
                    <i className="ri-shield-check-fill text-white text-lg"></i>
                  </span>
                  <div>
                    <p className="text-white font-label font-semibold text-sm">Garantie 10 ans</p>
                    <p className="text-white/70 text-xs font-body">Sur toutes nos réparations</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                    <i className="ri-timer-flash-fill text-white text-lg"></i>
                  </span>
                  <div>
                    <p className="text-white font-label font-semibold text-sm">Intervention 24-48h</p>
                    <p className="text-white/70 text-xs font-body">Dans tout l'Hérault</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                    <i className="ri-calendar-check-fill text-white text-lg"></i>
                  </span>
                  <div>
                    <p className="text-white font-label font-semibold text-sm">Disponible 7j/7</p>
                    <p className="text-white/70 text-xs font-body">De 8h à 20h</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right column - Form */}
          <div className="lg:w-7/12 p-6 md:p-8 lg:p-10">
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-50 mb-5">
                  <i className="ri-check-line text-accent-500 text-3xl"></i>
                </span>
                <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">
                  Merci pour votre message !
                </h3>
                <p className="text-secondary-500 font-body text-sm max-w-sm">
                  Notre équipe vous recontactera dans les plus brefs délais. Pour toute urgence, appelez-nous directement.
                </p>
                <a
                  href="tel:+33980802242"
                  className="btn-call mt-6 text-sm px-6 py-3"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line text-sm"></i>
                  </span>
                  09 80 80 22 42
                </a>
              </div>
            ) : (
              <form
                ref={formRef}
                id="d9j5ktohpes2mi34s6pg"
                data-readdy-form
                onSubmit={handleSubmit}
                className="h-full"
              >
                <h3 className="text-lg font-heading font-bold text-primary-500 mb-1">
                  Demandez votre devis gratuit
                </h3>
                <p className="text-secondary-500 font-body text-sm mb-6">
                  Réponse rapide, sans engagement.
                </p>

                {status === 'error' && (
                  <div className="mb-5 p-3 bg-accent-50 border border-accent-200 rounded-lg">
                    <p className="text-accent-600 font-body text-sm">{errorMsg}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Nom */}
                  <div>
                    <label htmlFor="contact_name" className="block text-xs font-label font-semibold text-primary-500 mb-1.5">
                      Nom complet <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact_name"
                      name="name"
                      required
                      className="w-full px-3.5 py-2.5 text-sm font-body border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 bg-background-50 text-foreground-500 placeholder:text-secondary-400 transition-all duration-200"
                      placeholder="Votre nom et prénom"
                    />
                  </div>

                  {/* Téléphone */}
                  <div>
                    <label htmlFor="contact_phone" className="block text-xs font-label font-semibold text-primary-500 mb-1.5">
                      Téléphone <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="contact_phone"
                      name="phone"
                      required
                      className="w-full px-3.5 py-2.5 text-sm font-body border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 bg-background-50 text-foreground-500 placeholder:text-secondary-400 transition-all duration-200"
                      placeholder="06 XX XX XX XX"
                    />
                  </div>

                  {/* Email */}
                  <div className="sm:col-span-2">
                    <label htmlFor="contact_email" className="block text-xs font-label font-semibold text-primary-500 mb-1.5">
                      Email <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact_email"
                      name="email"
                      required
                      className="w-full px-3.5 py-2.5 text-sm font-body border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 bg-background-50 text-foreground-500 placeholder:text-secondary-400 transition-all duration-200"
                      placeholder="votre@email.fr"
                    />
                  </div>

                  {/* Ville */}
                  <div className="sm:col-span-2">
                    <label htmlFor="contact_city" className="block text-xs font-label font-semibold text-primary-500 mb-1.5">
                      Votre ville dans l'Hérault <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact_city"
                      name="city"
                      required
                      className="w-full px-3.5 py-2.5 text-sm font-body border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 bg-background-50 text-foreground-500 placeholder:text-secondary-400 transition-all duration-200"
                      placeholder="Montpellier, Sète, Béziers..."
                    />
                  </div>

                  {/* Message */}
                  <div className="sm:col-span-2">
                    <label htmlFor="contact_message" className="block text-xs font-label font-semibold text-primary-500 mb-1.5">
                      Décrivez votre problème <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="contact_message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-3.5 py-2.5 text-sm font-body border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 bg-background-50 text-foreground-500 placeholder:text-secondary-400 resize-y transition-all duration-200"
                      placeholder="Type de menuiserie, nature de la panne, urgence..."
                    ></textarea>
                    <p className="text-xs text-secondary-400 mt-1 font-body">500 caractères maximum</p>
                  </div>
                </div>

                {/* Honeypot */}
                <div className="website_alt-wrap">
                  <label htmlFor="contact_alt">Ne pas remplir</label>
                  <input
                    type="text"
                    id="contact_alt"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="btn-call w-full mt-5 text-sm py-3 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? (
                    <>
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin text-sm"></i>
                      </span>
                      Envoi en cours...
                    </>
                  ) : (
                    'Envoyer ma demande de devis gratuit'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}